// src/stores/settings/cronjob.store.js
import { defineStore } from "pinia";
import { ref } from "vue";
import CronJobService from "@/services/settings/cronjob.service";
import { toast } from "@/utils/toast";

export const useCronJobStore = defineStore("cronjobs", () => {
  const rows = ref([]);
  const loading = ref(false);

  async function fetchList() {
    loading.value = true;
    try {
      const res = await CronJobService.getAll();
      rows.value = res.data || [];
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to load cron jobs", "error");
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      await CronJobService.create(payload);
      toast("Cron job created", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to create cron job", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      await CronJobService.update(id, payload);
      toast("Cron job updated", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to update cron job", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function toggle(id) {
    loading.value = true;
    try {
      await CronJobService.toggle(id);
      toast("Cron job status updated", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to toggle cron job", "error");
    } finally {
      loading.value = false;
    }
  }

  async function runNow(id) {
    loading.value = true;
    try {
      await CronJobService.runNow(id);
      toast("Cron job queued to run", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to run cron job", "error");
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    if (!ids?.length) return;
    loading.value = true;
    try {
      await CronJobService.bulkDelete(ids);
      toast("Cron jobs deleted", "success");
      await fetchList();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to delete cron jobs", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    rows,
    loading,
    fetchList,
    create,
    update,
    toggle,
    runNow,
    bulkDelete,
  };
});
